/** @format */

import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  useMediaQuery,
  Paper,
  Fade,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConfettiExplosion from "react-confetti-explosion";
import {
  trackEvent,
  trackConversion,
  EventNames,
  CustomDimensions,
  UserProperties,
} from "../utils/analytics";
import { useTracking, trackFormElement } from "../utils/trackingHooks";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    paddingTop: theme.spacing(25),
    paddingBottom: theme.spacing(8),
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(10),
  },
  title: {
    fontWeight: "500 !important",
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
  },
  subtitle: {
    fontWeight: 300,
    fontSize: "1.5rem !important",
    paddingTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem !important",
    },
  },
  benefitsText: {
    fontWeight: 300,
    fontSize: "1.3rem !important",
    paddingTop: theme.spacing(6),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem !important",
    },
  },
  checkMark: {
    color: "green",
    marginRight: "8px",
    fontSize: "16px",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  form: {
    marginTop: theme.spacing(8),
    width: "100%",
    maxWidth: "600px",
  },
  input: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      // backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderRadius: "4px",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.5)", // Added border color for unfocused state
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.9)", // White with opacity 0.9 for hover state
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "white",
    },
    "& .MuiInputBase-input": {
      color: "white",
    },
  },

  submitButton: {
    width: "35%",
    height: "45px",
    borderRadius: "55px !important",
    backgroundColor: "rgba(216, 216, 216, 1) !important",
    boxShadow: "none !important",
    color: "#fff",
    transition: "all 0.3s ease-in !important",
    "&:not([disabled])": {
      background:
        "linear-gradient(to right, #FF9250, #FF6473, #FE26A0, #CF34B9, #7852DC, #017AFF) !important",
    },
    "&:hover": {
      background:
        "linear-gradient(to right, #FF9250, #FF6473, #FE26A0, #CF34B9, #7852DC, #017AFF) !important",
    },
    "&:not([disabled]):hover": {
      "& .MuiButton-root": {
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        transition: "text-decoration 0.3s ease-in",
      },
    },
  },
  // New styles for success component
  successContainer: {
    display: "flex",
    flex: 1,
    minHeight: "70vh",
    flexDirection: "column",
    alignItems: "center",
    // justifyContent: "center",
    width: "100%",
    maxWidth: "700px",
    padding: theme.spacing(4),
  },
  successIcon: {
    fontSize: "70px !important",
    margin: theme.spacing(2, 0, 4),
    background:
      "linear-gradient(to right, #FF9250, #FF6473, #FE26A0, #CF34B9, #7852DC, #017AFF)",
    borderRadius: "50%",
    padding: theme.spacing(0.3),
    boxShadow: "0 4px 20px 0 rgba(255,255,255,0.42)",
    // color of the icon
    color: "#ddd",
  },
  successTitle: {
    fontWeight: "600 !important",
    fontSize: "2.5rem !important",
    marginBottom: theme.spacing(50),
    paddingBottom: theme.spacing(5),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    background:
      "linear-gradient(to right, #FF9250, #FF6473, #FE26A0, #CF34B9, #7852DC, #017AFF)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem !important",
    },
  },
  successSubtitle: {
    fontWeight: 300,
    fontSize: "1.5rem !important",
    paddingBottom: theme.spacing(5),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem !important",
    },
  },
  successMessage: {
    fontWeight: 300,
    fontSize: "1.1rem !important",
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    maxWidth: "600px",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem !important",
    },
  },
  highlightText: {
    fontWeight: 500,
    color: "#FF6473",
  },
  successCard: {
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
    border: "1px solid rgba(255, 255, 255, 0.2)",
    width: "100%",
    maxWidth: "500px",
  },
}));

const pilotMessage = (
  <>
    Be the first to experience the future of healthy longevity with our beta
    App.
    <br />
    Join the waitlist now for exclusive early access!
  </>
);

const pilotExtraMessage = [
  "Be the first to experience new features",
  "Unlock exclusive deals",
  "Influence the Future with Your Feedback",
  "👉 Secure Your Spot Today!",
];

const PilotTest = () => {
  // Use our custom tracking hook for this component
  const { trackAction, trackComponentView } = useTracking("PilotTestForm");

  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const initialFormState = {
    name: "",
    email: "",
    message: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const formRef = useRef<HTMLFormElement>(null);

  // Track page view and component render
  useEffect(() => {
    // Track component view when component loads
    trackComponentView();

    // Track page engagement
    trackEvent("waitlist_page_view", {
      device_type: isMobile ? "mobile" : "desktop",
      [CustomDimensions.PAGE_TEMPLATE]: "waitlist",
    });

    // Track UTM parameters if available
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");

    if (utmSource || utmMedium || utmCampaign) {
      trackEvent("utm_parameters", {
        utm_source: utmSource || "direct",
        utm_medium: utmMedium || "none",
        utm_campaign: utmCampaign || "none",
        landing_page: "pilot_test",
      });
    }

    // Expose waitlist function to window for tracking
    const trackWaitlistInteraction = (action: string, details?: any) => {
      trackEvent("waitlist_interaction", {
        action,
        ...(details || {}),
      });
    };

    (window as any).trackWaitlistInteraction = trackWaitlistInteraction;

    return () => {
      // Clean up
      delete (window as any).trackWaitlistInteraction;
    };
  }, [isMobile]);

  // Function to check if name and email fields are filled
  const isFormValid = () => {
    const isValidEmail =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
        formData.email.trim()
      );
    return formData.name.trim() !== "" && isValidEmail;
  };

  // Track when fields are changed
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
      isPilot: true,
    }));

    // Track field changes for analytics
    trackFormElement("waitlist_form", name, "change", {
      field_length: value.length,
      is_valid:
        name === "email"
          ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          : value.length > 0,
    });
  };

  const resetForm = () => {
    setFormData(initialFormState);
    trackEvent("form_reset", { form_name: "waitlist_form" });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    trackEvent("notification_close", {
      notification_type: snackbar.severity,
      message: snackbar.message,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission start
    trackEvent(EventNames.FORM_START, {
      form_name: "waitlist_form",
      email_domain: formData.email.split("@")[1] || "",
      has_message: formData.message.trim().length > 0,
    });

    const startTime = new Date().getTime();

    try {
      const response = await fetch("https://api.eon.health/contact_form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const responseTime = new Date().getTime() - startTime;

      if (response.ok) {
        // Track successful conversion
        trackConversion(EventNames.PILOT_TEST_REQUEST, {
          user_name: formData.name,
          email_domain: formData.email.split("@")[1] || "",
          has_message: formData.message.trim().length > 0,
          response_time_ms: responseTime,
          form_completion_time_ms: responseTime,
        });

        // Enhanced user properties for this conversion
        const setUserProps = {
          [UserProperties.USER_TYPE]: "early_access",
          [UserProperties.USER_SEGMENT]: "waitlist_member",
        };

        // Set user properties
        trackEvent("user_properties_update", setUserProps);

        // Track success view
        trackEvent("waitlist_success", {
          timestamp: new Date().toISOString(),
        });

        // Instead of showing snackbar, set success state to true
        setSubmissionSuccess(true);
        setIsExploding(true);
        resetForm();
      } else {
        // Track form submission failure
        trackEvent(EventNames.FORM_ERROR, {
          form_name: "waitlist_form",
          error_type: "api_error",
          http_status: response.status,
          response_time_ms: responseTime,
        });

        setSnackbar({
          open: true,
          message: "Failed to send message. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      // Track network error
      trackEvent(EventNames.FORM_ERROR, {
        form_name: "waitlist_form",
        error_type: "network_error",
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      setSnackbar({
        open: true,
        message: "An unexpected error occurred. Please try again later.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success component that will replace the form
  const SuccessView = () => {
    // Track when success view is shown
    useEffect(() => {
      // Track success view impressions
      trackEvent("success_view_impression", {
        page: "waitlist",
        timestamp: new Date().toISOString(),
      });

      // Track when confetti animation ends
      const confettiTimer = setTimeout(() => {
        trackEvent("waitlist_confetti_complete", {
          duration_ms: 3000,
        });
      }, 3000);

      return () => {
        clearTimeout(confettiTimer);
      };
    }, []);

    return (
      <Fade
        in={submissionSuccess}
        timeout={800}
        onEntered={() => {
          trackEvent("success_animation_complete", {
            animation: "fade_in",
            duration_ms: 800,
          });
        }}
      >
        <Box className={classes.successContainer}>
          {isExploding && (
            <ConfettiExplosion
              force={0.8}
              duration={3000}
              particleCount={100}
              width={1600}
            />
          )}

          <CheckCircleIcon
            className={classes.successIcon}
            onClick={() => {
              trackEvent("success_icon_click", {
                element: "check_circle_icon",
              });
            }}
          />

          <Typography
            variant="h2"
            className={classes.successTitle}
            onClick={() =>
              trackEvent("text_click", { text_type: "success_title" })
            }
          >
            You're in the Waitlist!
          </Typography>

          <Typography
            variant="body1"
            className={classes.successSubtitle}
            onClick={() =>
              trackEvent("text_click", { text_type: "success_subtitle" })
            }
          >
            Thank you for joining our exclusive beta program!
          </Typography>

          <Typography
            variant="body2"
            className={classes.successMessage}
            onClick={() =>
              trackEvent("text_click", { text_type: "success_message" })
            }
          >
            We'll notify you via email when your exclusive access is ready.
          </Typography>
        </Box>
      </Fade>
    );
  };

  return (
    <Box className={classes.root}>
      <Container maxWidth="lg">
        <Box className={classes.content}>
          {submissionSuccess ? (
            <SuccessView />
          ) : (
            <>
              <Typography variant="h2" className={classes.title}>
                {"🚀 Get Early Access!"}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {pilotMessage}
              </Typography>

              <Typography variant="body1" className={classes.benefitsText}>
                {pilotExtraMessage.map((message, index) => (
                  <Box key={index} display="flex" alignItems="center">
                    {index < pilotExtraMessage.length - 1 && (
                      <Box
                        component="span"
                        display="inline-block"
                        className={classes.checkMark}
                      >
                        ✓
                      </Box>
                    )}
                    {message}
                  </Box>
                ))}
              </Typography>

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className={classes.form}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() =>
                        trackFormElement("waitlist_form", "name", "focus")
                      }
                      onBlur={() =>
                        trackFormElement("waitlist_form", "name", "blur", {
                          length: formData.name.length,
                          is_valid: formData.name.trim() !== "",
                        })
                      }
                      className={classes.input}
                      disabled={isSubmitting}
                      inputProps={{
                        "aria-label": "Your name",
                        "data-testid": "waitlist-name-input",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() =>
                        trackFormElement("waitlist_form", "email", "focus")
                      }
                      onBlur={() =>
                        trackFormElement("waitlist_form", "email", "blur", {
                          length: formData.email.length,
                          is_valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            formData.email
                          ),
                          domain: formData.email.split("@")[1] || "",
                        })
                      }
                      className={classes.input}
                      disabled={isSubmitting}
                      inputProps={{
                        "aria-label": "Your email address",
                        "data-testid": "waitlist-email-input",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message (Optional)"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      onFocus={() =>
                        trackFormElement("waitlist_form", "message", "focus")
                      }
                      onBlur={() =>
                        trackFormElement("waitlist_form", "message", "blur", {
                          length: formData.message.length,
                          has_content: formData.message.trim() !== "",
                        })
                      }
                      className={classes.input}
                      disabled={isSubmitting}
                      inputProps={{
                        "aria-label": "Your message (optional)",
                        "data-testid": "waitlist-message-input",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        className={classes.submitButton}
                        disabled={isSubmitting || !isFormValid()}
                        onClick={() => {
                          if (!isFormValid()) {
                            trackEvent("button_disabled_click", {
                              button_name: "join_waitlist",
                              reason: !formData.name.trim()
                                ? "missing_name"
                                : "invalid_email",
                              form_name: "waitlist_form",
                            });
                          } else {
                            trackEvent(EventNames.BUTTON_CLICK, {
                              button_name: "join_waitlist",
                              location: "waitlist_form",
                            });
                          }
                        }}
                        sx={{
                          position: "relative",
                          "&:not([disabled])": {
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              width: "0%",
                              height: "1px",
                              bottom: "6px",
                              left: "50%",
                              backgroundColor: "#fff",
                              transform: "translateX(-50%)",
                              transition: "width 0.3s ease-in",
                            },
                            "&:hover::after": {
                              width: "70%",
                            },
                          },
                        }}
                        data-testid="waitlist-submit-button"
                      >
                        {isSubmitting ? "Joining..." : "Join Waitlist"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </>
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </Box>
  );
};

export default PilotTest;
